import { RefreshCcw, Receipt, Search, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getCourses, getStudents, getTransactions } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function formatStudentName(record) {
  return record?.fullName
    || `${record?.firstName || ''} ${record?.lastName || ''}`.trim()
    || record?.bio
    || 'Unnamed student';
}

function getProfileKeys(record) {
  const keys = [];
  if (record?.id) keys.push(`id:${record.id}`);
  if (record?.email) keys.push(`email:${normalizeText(record.email)}`);
  if (record?.authUid) keys.push(`auth:${record.authUid}`);
  const name = formatStudentName(record);
  if (name) keys.push(`name:${normalizeText(name)}`);
  return keys;
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addDays(value, days) {
  const date = toDate(value);
  if (!date) return null;
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function resolveAdmissionDate(record) {
  return toDate(record?.admissionDate)
    || toDate(record?.admittedAt)
    || toDate(record?.joinedAt)
    || toDate(record?.createdAt)
    || null;
}

function moneyValue(value) {
  return Number(value || 0);
}

function makeInitials(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'ST';
}

function buildProfile(record, source, courseLookup = new Map()) {
  const courseName = record.courseName || courseLookup.get(record.courseId)?.name || '';
  const admissionDate = resolveAdmissionDate(record);
  const courseAmount = moneyValue(record.courseFeeAmount || record.totalFee || record.courseAmount || courseLookup.get(record.courseId)?.feeAmount || 0);
  const label = formatStudentName(record);
  const applicationId = source === 'signup'
    ? record.id
    : record.applicationId || record.signupId || '';
  return {
    id: `${source}:${record.id}`,
    keys: new Set(getProfileKeys(record)),
    label,
    applicationId,
    email: record.email || '',
    phone: record.phone || '',
    courseId: record.courseId || '',
    courseName,
    courseAmount,
    intakeLabel: record.intakeLabel || '',
    status: record.status || '',
    admissionDate,
    balanceDueDate: admissionDate ? addDays(admissionDate, 30) : null,
    source,
    transactionCount: 0,
    paidTotal: 0,
    lastPaidAt: null,
    lastActivityAt: null,
  };
}

function mergeProfile(profile, record, courseLookup = new Map(), source = '') {
  profile.keys = new Set([...profile.keys, ...getProfileKeys(record)]);
  profile.label = profile.label || formatStudentName(record);
  profile.applicationId = profile.applicationId || (source === 'signup' ? record.id : '') || record.applicationId || record.signupId || '';
  profile.email = profile.email || record.email || '';
  profile.phone = profile.phone || record.phone || '';
  profile.courseId = profile.courseId || record.courseId || '';
  profile.courseName = profile.courseName || record.courseName || courseLookup.get(record.courseId)?.name || '';
  profile.courseAmount = profile.courseAmount || moneyValue(record.courseFeeAmount || record.totalFee || record.courseAmount || courseLookup.get(record.courseId)?.feeAmount || 0);
  profile.intakeLabel = profile.intakeLabel || record.intakeLabel || '';
  profile.status = profile.status || record.status || '';
  const admissionDate = profile.admissionDate || resolveAdmissionDate(record);
  profile.admissionDate = admissionDate;
  profile.balanceDueDate = admissionDate ? addDays(admissionDate, 30) : profile.balanceDueDate;
}

function profileMatchesTransaction(profile, transaction) {
  const transactionKeys = [
    transaction?.studentId ? `id:${transaction.studentId}` : '',
    transaction?.studentEmail ? `email:${normalizeText(transaction.studentEmail)}` : '',
    transaction?.studentName ? `name:${normalizeText(transaction.studentName)}` : '',
  ].filter(Boolean);

  return transactionKeys.some((key) => profile.keys.has(key));
}

function buildProfiles(students = [], signups = [], transactions = [], courseLookup = new Map()) {
  const profiles = [];
  const index = new Map();

  function upsert(record, source) {
    const keys = getProfileKeys(record);
    const existing = keys.map((key) => index.get(key)).find(Boolean);

    if (existing) {
      mergeProfile(existing, record, courseLookup, source);
      keys.forEach((key) => index.set(key, existing));
      return existing;
    }

    const profile = buildProfile(record, source, courseLookup);
    profiles.push(profile);
    profile.keys.forEach((key) => index.set(key, profile));
    return profile;
  }

  students.forEach((item) => upsert(item, 'student'));
  signups.forEach((item) => upsert(item, 'signup'));

  transactions.forEach((transaction) => {
    const matched = profiles.find((profile) => profileMatchesTransaction(profile, transaction));
    if (matched) {
      matched.keys.forEach((key) => index.set(key, matched));
      return;
    }

    if (!transaction.studentName && !transaction.studentEmail && !transaction.studentId) return;

    const synthetic = {
      id: transaction.studentId || transaction.studentEmail || transaction.studentName,
      fullName: transaction.studentName || transaction.studentEmail || 'Unknown student',
      email: transaction.studentEmail || '',
      phone: transaction.studentPhone || '',
      courseId: transaction.courseId || '',
      courseName: transaction.courseName || '',
      courseAmount: transaction.courseAmount || 0,
      intakeLabel: '',
      status: 'active',
    };
    const profile = buildProfile(synthetic, 'transaction', courseLookup);
    profiles.push(profile);
    profile.keys.forEach((key) => index.set(key, profile));
  });

  transactions.forEach((transaction) => {
    const amount = moneyValue(transaction.amount);
    const paidAt = toDate(transaction.paymentDate) || toDate(transaction.createdAt);
    const invoiceAt = toDate(transaction.createdAt);
    const matchedProfiles = profiles.filter((profile) => profileMatchesTransaction(profile, transaction));

    matchedProfiles.forEach((profile) => {
      profile.transactionCount += 1;
      profile.lastActivityAt = [profile.lastActivityAt, paidAt || invoiceAt].filter(Boolean).sort((a, b) => b - a)[0] || profile.lastActivityAt;

      if (String(transaction.category || '') === 'school_fee' || String(transaction.type || '') === 'revenue') {
        profile.paidTotal += amount;
        profile.lastPaidAt = [profile.lastPaidAt, paidAt].filter(Boolean).sort((a, b) => b - a)[0] || profile.lastPaidAt;
      }

      profile.courseId = profile.courseId || transaction.courseId || '';
      profile.courseName = profile.courseName || transaction.courseName || courseLookup.get(transaction.courseId)?.name || '';
      profile.courseAmount = profile.courseAmount || moneyValue(transaction.courseAmount || transaction.courseFeeAmount || transaction.totalFee || courseLookup.get(transaction.courseId)?.feeAmount || 0);
    });
  });

  return profiles.map((profile) => {
    const courseAmount = moneyValue(profile.courseAmount || courseLookup.get(profile.courseId)?.feeAmount || 0);
    const balance = Math.max(courseAmount - profile.paidTotal, 0);
    const overdue = balance > 0;
    return {
      id: profile.id,
      label: profile.label,
      applicationId: profile.applicationId || '',
      email: profile.email,
      phone: profile.phone,
      courseName: profile.courseName || courseLookup.get(profile.courseId)?.name || '—',
      courseAmount,
      paidTotal: profile.paidTotal,
      balance,
      transactionCount: profile.transactionCount,
      lastPaidAt: profile.lastPaidAt,
      lastActivityAt: profile.lastActivityAt,
      admissionDate: profile.admissionDate,
      balanceDueDate: profile.balanceDueDate,
      status: overdue ? 'Outstanding' : 'Cleared',
    };
  });
}

export function FeeBalancesPage() {
  const { activeTenantId, activeTenant } = useTenant();
  const currency = activeTenant?.settings?.currency || 'KES';

  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setStudents([]);
      setSignups([]);
      setCourses([]);
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextStudents, nextSignups, nextCourses, nextTransactions] = await Promise.all([
        getStudents(activeTenantId),
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
        getTransactions(activeTenantId),
      ]);
      setStudents(nextStudents);
      setSignups(nextSignups);
      setCourses(nextCourses);
      setTransactions(nextTransactions);
    } catch (error) {
      console.error('Unable to load fee balances', error);
      setStudents([]);
      setSignups([]);
      setCourses([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const profiles = useMemo(
    () => buildProfiles(students, signups, transactions, new Map(courses.map((course) => [course.id, course]))),
    [courses, signups, students, transactions],
  );

  const allRows = useMemo(() => {
    return profiles.sort((a, b) => b.balance - a.balance || a.label.localeCompare(b.label));
  }, [profiles]);

  const balanceRows = useMemo(() => {
    const search = normalizeText(searchTerm);
    return allRows
      .filter((item) => {
        if (!search) return true;
        return [
          item.label,
          item.email,
          item.phone,
          item.courseName,
          item.status,
        ].some((value) => normalizeText(value).includes(search));
      })
      .sort((a, b) => b.balance - a.balance || a.label.localeCompare(b.label));
  }, [allRows, searchTerm]);

  const outstandingTotal = useMemo(
    () => allRows.reduce((sum, item) => sum + item.balance, 0),
    [allRows],
  );

  const invoiceTotal = useMemo(
    () => allRows.reduce((sum, item) => sum + item.courseAmount, 0),
    [allRows],
  );

  const paidTotal = useMemo(
    () => allRows.reduce((sum, item) => sum + item.paidTotal, 0),
    [allRows],
  );

  const studentCount = allRows.length;
  const courseCount = new Set(allRows.map((item) => item.courseName).filter(Boolean)).size;
  const latestActivity = allRows
    .map((item) => item.lastActivityAt)
    .filter(Boolean)
    .sort((a, b) => b - a)[0] || null;
  const clearedCount = allRows.filter((item) => item.balance <= 0).length;
  const outstandingCount = allRows.length - clearedCount;

  if (loading) {
    return <LoadingState label="Loading fee balances..." />;
  }

  return (
    <div className="page-stack fee-balance-page">
      <section className="fee-balance-hero">
        <div className="fee-balance-hero-copy">
          <div className="fee-balance-hero-title-row">
            <h5>Fee Balances</h5>
            <span className="fee-balance-hero-chip">{studentCount} students</span>
          </div>
          <p className="fee-balance-hero-text">
            Track total course fees, what has been paid, and what still needs to be cleared.
          </p>
          <div className="fee-balance-hero-meta">
            <span>{courseCount} courses represented</span>
            <span className="fee-balance-inline-dot" aria-hidden="true" />
            <span>{outstandingCount} outstanding</span>
            <span className="fee-balance-inline-dot" aria-hidden="true" />
            <span>{clearedCount} cleared</span>
          </div>
        </div>
        <div className="fee-balance-hero-actions">
          <Button to="/dashboard/finance/invoices" variant="secondary" icon={Receipt}>Open Invoices</Button>
          <Button onClick={load} variant="secondary" icon={RefreshCcw}>Refresh</Button>
        </div>
      </section>

      <section className="fee-balance-stats">
        <article className="fee-balance-stat-card">
          <span className="fee-balance-stat-label">Outstanding</span>
          <strong>{formatCurrency(outstandingTotal, currency)}</strong>
          <p>Total unpaid balance across all students.</p>
        </article>
        <article className="fee-balance-stat-card">
          <span className="fee-balance-stat-label">Students</span>
          <strong>{studentCount}</strong>
          <p>All students currently included in the finance view.</p>
        </article>
        <article className="fee-balance-stat-card">
          <span className="fee-balance-stat-label">Course Amount</span>
          <strong>{formatCurrency(invoiceTotal, currency)}</strong>
          <p>Total amount expected across enrolled courses.</p>
        </article>
        <article className="fee-balance-stat-card">
          <span className="fee-balance-stat-label">Paid</span>
          <strong>{formatCurrency(paidTotal, currency)}</strong>
          <p>{latestActivity ? `Latest activity ${formatDateTime(latestActivity)}` : 'No payment activity yet.'}</p>
        </article>
      </section>

      <section className="fee-balance-toolbar">
        <div className="fee-balance-toolbar-meta">
          <span className="fee-balance-toolbar-title">Student fee balances</span>
          <span className="fee-balance-toolbar-text">{balanceRows.length} visible rows</span>
        </div>
        <label className="balance-search input-group" htmlFor="fee-balance-search">
          <span className="input-group-text" aria-hidden="true"><Search size={16} /></span>
          <input
            id="fee-balance-search"
            type="search"
            placeholder="Search student, course, phone..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>
      </section>

      <section className="fee-balance-table-card">
        <div className="fee-balance-table-header">
          <div>
            <h6>Balances by student</h6>
            <p>Every student is shown here. Scroll inside the table if you need more room.</p>
          </div>
          <div className="fee-balance-table-summary">
            <span>{courseCount} courses</span>
            <span>{outstandingCount} outstanding</span>
          </div>
        </div>

        <div className="fee-balance-table-wrap">
          {balanceRows.length ? (
            <table className="feeBalanceRecordsTable">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Due Date</th>
                  <th>View student</th>
                </tr>
              </thead>
              <tbody>
                {balanceRows.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="fee-balance-student">
                        <span className="fee-balance-avatar" aria-hidden="true">{makeInitials(item.label)}</span>
                        <div>
                          <strong>{item.label}</strong>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="fee-balance-course-pill">{item.courseName || '—'}</span>
                    </td>
                    <td>
                      <strong className="fee-balance-amount">{formatCurrency(item.paidTotal, currency)}</strong>
                    </td>
                    <td>
                      <strong className="fee-balance-value">{formatCurrency(item.balance, currency)}</strong>
                    </td>
                    <td>
                      <div className="fee-balance-date-cell">
                        <strong>{item.balanceDueDate ? formatDate(item.balanceDueDate) : '—'}</strong>
                      </div>
                    </td>
                    <td>
                      <Button to={`/view-student?applicationId=${encodeURIComponent(item.applicationId || item.id)}`} variant="secondary" className="fee-balance-view-btn">
                        View student
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState
              title="No students found."
              message="Student fee balances will appear here once admissions and payments are recorded."
              icon={Wallet}
            />
          )}
        </div>
      </section>
    </div>
  );
}
